import React, { Component } from 'react';
import { Row, Col, Button, Input, InputNumber, Tag, Modal, message, Tooltip, Icon } from 'antd';
import { postCreatePreAuthLink, postCaptureDeposit, postReleaseDeposit } from '../../../../utils/apis/securityDeposit';
import { PRE_AUTH_MAX_NIGHTS, isStayBeyondPreAuthWindow } from './preAuthWindow';
import './PreAuthDepositSection.css';

const STATUS_CONFIG = {
  pending: { color: '#faad14', label: 'Pending Authorization' },
  authorized: { color: '#1890ff', label: 'Authorized (Hold Active)' },
  captured: { color: '#f5222d', label: 'Charged' },
  released: { color: '#52c41a', label: 'Released' },
  expired: { color: '#d9d9d9', label: 'Link Expired' },
  failed: { color: '#ff4d4f', label: 'Failed' }
};

class PreAuthDepositSection extends Component {
  state = {
    depositAmount: this.props.defaultAmount || undefined,
    captureAmount: undefined,
    isGenerating: false,
    isCapturing: false,
    isReleasing: false,
    linkCopied: false
  };

  componentDidUpdate(prevProps) {
    if (prevProps.defaultAmount !== this.props.defaultAmount && !this.props.securityDeposit.status) {
      this.setState({ depositAmount: this.props.defaultAmount || undefined });
    }
  }

  handleGenerateLink = async () => {
    const { reservationId, onStatusChange } = this.props;
    const { depositAmount } = this.state;

    if (!depositAmount || depositAmount <= 0) {
      message.error('Please enter a deposit amount greater than 0');
      return;
    }

    this.setState({ isGenerating: true });
    try {
      const result = await postCreatePreAuthLink(reservationId, {
        amount: depositAmount,
        successUrl: `${window.location.origin}/deposit/confirmation?status=success`,
        cancelUrl: `${window.location.origin}/deposit/confirmation?status=cancelled`
      });
      message.success('Pre-authorization link generated successfully');
      if (onStatusChange) onStatusChange();
    } catch (err) {
      message.error(err.message || 'Failed to generate pre-authorization link');
    } finally {
      this.setState({ isGenerating: false });
    }
  };

  handleCopyLink = () => {
    const { securityDeposit } = this.props;
    if (securityDeposit.stripeCheckoutSessionUrl) {
      navigator.clipboard.writeText(securityDeposit.stripeCheckoutSessionUrl).then(() => {
        this.setState({ linkCopied: true });
        message.success('Link copied to clipboard');
        setTimeout(() => this.setState({ linkCopied: false }), 3000);
      });
    }
  };

  handleCapture = async () => {
    const { reservationId, securityDeposit, onStatusChange } = this.props;
    const { captureAmount } = this.state;

    if (!captureAmount || captureAmount <= 0) {
      message.error('Please enter a charge amount greater than 0');
      return;
    }

    if (captureAmount > securityDeposit.authorizedAmount) {
      message.error(`Cannot charge more than authorized amount (${securityDeposit.authorizedAmount})`);
      return;
    }

    Modal.confirm({
      title: 'Charge Security Deposit',
      content: `Are you sure you want to charge ${this.props.currency}${captureAmount} from the ${this.props.currency}${
        securityDeposit.authorizedAmount
      } hold? The remaining ${this.props.currency}${(securityDeposit.authorizedAmount - captureAmount).toFixed(2)} will be released.`,
      onOk: async () => {
        this.setState({ isCapturing: true });
        try {
          await postCaptureDeposit(reservationId, { amount: captureAmount });
          message.success('Deposit charged successfully');
          if (onStatusChange) onStatusChange();
        } catch (err) {
          message.error(err.message || 'Failed to charge deposit');
        } finally {
          this.setState({ isCapturing: false });
        }
      }
    });
  };

  handleRelease = async () => {
    const { reservationId, securityDeposit, onStatusChange } = this.props;

    Modal.confirm({
      title: 'Release Security Deposit Hold',
      content: `Are you sure you want to release the entire ${this.props.currency}${securityDeposit.authorizedAmount} hold? This action cannot be undone.`,
      onOk: async () => {
        this.setState({ isReleasing: true });
        try {
          await postReleaseDeposit(reservationId);
          message.success('Deposit hold released successfully');
          if (onStatusChange) onStatusChange();
        } catch (err) {
          message.error(err.message || 'Failed to release deposit hold');
        } finally {
          this.setState({ isReleasing: false });
        }
      }
    });
  };

  renderNoStatus() {
    const { currency, checkIsAdminReadOnly, checkInDate, checkOutDate } = this.props;
    const { depositAmount, isGenerating } = this.state;
    const isReadOnly = checkIsAdminReadOnly && checkIsAdminReadOnly();
    const stayTooLong = isStayBeyondPreAuthWindow(checkInDate, checkOutDate);
    const isDisabled = isReadOnly || stayTooLong;

    const stayTooLongTooltip = `Pre-auth is unavailable for stays longer than ${PRE_AUTH_MAX_NIGHTS} nights. A Stripe hold lasts only ${PRE_AUTH_MAX_NIGHTS} days and would release while the guest is still in stay.`;
    const defaultTooltip = 'Card holds last up to 7 days (31 with extended auth). For future bookings, generate closer to check-in.';

    return (
      <div>
        <Row className="preauth-deposit-row">
          <div className="preauth-deposit-label">Pre-Auth Security Deposit Amount</div>
          <Tooltip title={stayTooLong ? stayTooLongTooltip : ''}>
            <Input
              addonBefore={currency}
              type="number"
              step="0.01"
              min={0}
              value={depositAmount}
              placeholder="Enter deposit amount"
              onChange={e => this.setState({ depositAmount: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
              style={{ width: '100%', marginBottom: 8 }}
              disabled={isDisabled}
            />
          </Tooltip>
        </Row>
        <Row>
          <Tooltip title={stayTooLong ? stayTooLongTooltip : defaultTooltip}>
            {/* span wrapper so the tooltip still fires when the button is disabled */}
            <span style={{ display: 'block', width: '100%' }}>
              <Button
                type="primary"
                size="large"
                style={{ width: '100%' }}
                onClick={this.handleGenerateLink}
                loading={isGenerating}
                disabled={!depositAmount || depositAmount <= 0 || isDisabled}
                icon="link"
              >
                Generate Pre-Auth Link
              </Button>
            </span>
          </Tooltip>
        </Row>
      </div>
    );
  }

  renderPending() {
    const { securityDeposit, currency } = this.props;
    const { linkCopied } = this.state;

    return (
      <div>
        <Row className="preauth-deposit-row">
          <Tag color={STATUS_CONFIG.pending.color}>{STATUS_CONFIG.pending.label}</Tag>
          <div className="preauth-deposit-amount">
            {currency}
            {securityDeposit.amount}
          </div>
        </Row>
        <Row className="preauth-deposit-row">
          <Button icon={linkCopied ? 'check' : 'copy'} onClick={this.handleCopyLink} style={{ width: '100%', marginBottom: 8 }}>
            {linkCopied ? 'Link Copied!' : 'Copy Payment Link'}
          </Button>
        </Row>
        <Row>
          <Button
            type="danger"
            size="small"
            onClick={() => {
              Modal.confirm({
                title: 'Cancel Pre-Auth Link',
                content: 'Are you sure you want to cancel this pre-authorization link? You can generate a new one after.',
                onOk: this.handleGenerateLink
              });
            }}
          >
            Cancel & Regenerate
          </Button>
        </Row>
      </div>
    );
  }

  renderAuthorized() {
    const { securityDeposit, currency, checkIsAdminReadOnly } = this.props;
    const { captureAmount, isCapturing, isReleasing } = this.state;
    const isDisabled = checkIsAdminReadOnly && checkIsAdminReadOnly();

    const amountMismatch = securityDeposit.amount !== securityDeposit.authorizedAmount;

    return (
      <div>
        <Row className="preauth-deposit-row">
          <Tag color={STATUS_CONFIG.authorized.color}>{STATUS_CONFIG.authorized.label}</Tag>
          <div className="preauth-deposit-amount">
            {currency}
            {securityDeposit.authorizedAmount}
          </div>
        </Row>
        {amountMismatch && (
          <Row className="preauth-deposit-warning">
            <Icon type="warning" style={{ color: '#faad14', marginRight: 4 }} />
            Desired deposit ({currency}
            {securityDeposit.amount}) differs from authorized hold ({currency}
            {securityDeposit.authorizedAmount}).
          </Row>
        )}
        <Row className="preauth-deposit-row">
          <div className="preauth-deposit-label">Charge Amount</div>
          <Input
            addonBefore={currency}
            type="number"
            step="0.01"
            min={0.01}
            max={securityDeposit.authorizedAmount}
            value={captureAmount}
            placeholder="Enter amount to charge"
            onChange={e => this.setState({ captureAmount: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
            style={{ width: '100%', marginBottom: 8 }}
            disabled={isDisabled}
          />
        </Row>
        <Row className="preauth-deposit-actions">
          <Button
            type="primary"
            onClick={this.handleCapture}
            loading={isCapturing}
            disabled={!captureAmount || captureAmount <= 0 || isDisabled}
            style={{ marginRight: 8 }}
          >
            Charge
          </Button>
          <Button type="danger" onClick={this.handleRelease} loading={isReleasing} disabled={isDisabled}>
            Release Hold
          </Button>
        </Row>
      </div>
    );
  }

  renderCaptured() {
    const { securityDeposit, currency } = this.props;
    return (
      <div>
        <Row className="preauth-deposit-row">
          <Tag color={STATUS_CONFIG.captured.color}>{STATUS_CONFIG.captured.label}</Tag>
          <div className="preauth-deposit-amount">
            {currency}
            {securityDeposit.capturedAmount}
          </div>
        </Row>
        <Row className="preauth-deposit-info">
          Original hold: {currency}
          {securityDeposit.authorizedAmount}
          {securityDeposit.authorizedAmount > securityDeposit.capturedAmount && (
            <>
              {' · Released back to guest: '}
              {currency}
              {(securityDeposit.authorizedAmount - securityDeposit.capturedAmount).toFixed(2)}
            </>
          )}
        </Row>
      </div>
    );
  }

  renderReleased() {
    const { securityDeposit, currency } = this.props;
    return (
      <div>
        <Row className="preauth-deposit-row">
          <Tag color={STATUS_CONFIG.released.color}>{STATUS_CONFIG.released.label}</Tag>
          <div className="preauth-deposit-amount">
            {currency}
            {securityDeposit.authorizedAmount}
          </div>
        </Row>
      </div>
    );
  }

  renderExpired() {
    return (
      <div>
        <Row className="preauth-deposit-row">
          <Tag color={STATUS_CONFIG.expired.color}>{STATUS_CONFIG.expired.label}</Tag>
        </Row>
        <Row>{this.renderNoStatus()}</Row>
      </div>
    );
  }

  renderFailed() {
    return (
      <div>
        <Row className="preauth-deposit-row">
          <Tag color={STATUS_CONFIG.failed.color}>{STATUS_CONFIG.failed.label}</Tag>
        </Row>
        <Row>{this.renderNoStatus()}</Row>
      </div>
    );
  }

  render() {
    const { securityDeposit } = this.props;
    const status = securityDeposit ? securityDeposit.status : '';

    return (
      <div className="preauth-deposit-section">
        <div className="preauth-deposit-title">Stripe Pre-Auth Security Deposit</div>
        {(!status || status === '') && this.renderNoStatus()}
        {status === 'pending' && this.renderPending()}
        {status === 'authorized' && this.renderAuthorized()}
        {status === 'captured' && this.renderCaptured()}
        {status === 'released' && this.renderReleased()}
        {status === 'expired' && this.renderExpired()}
        {status === 'failed' && this.renderFailed()}
      </div>
    );
  }
}

export default PreAuthDepositSection;
